import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import axios from "axios";
import { useLang } from "../context/LanguageContext";
import usePhotos from "../hooks/usePhotos";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IMG_1 =
  "https://images.unsplash.com/photo-1599837705379-c38d277d6cbb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxkcnklMjBhZ2VkJTIwYmVlZiUyMHN0ZWFrJTIwbWVhdCUyMGRpc3BsYXklMjBzaG93Y2FzZSUyMGdvdXJtZXR8ZW58MHx8fHwxNzkyNjA2MTYxfDA&ixlib=rb-4.1.0&q=85";
const IMG_2 =
  "https://images.unsplash.com/photo-1606677661991-446cea8ee182?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHxfHxkcnklMjBhZ2VkJTIwYmVlZiUyMHN0ZWFrJTIwbWVhdCUyMGRpc3BsYXklMjBzaG93Y2FzZSUyMGdvdXJtZXR8ZW58MHx8fHwxNzkyNjA2MTYxfDA&ixlib=rb-4.1.0&q=85";
const IMG_3 =
  "https://images.unsplash.com/photo-1622992599912-d5ce51fe395b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHw0fHxkcnklMjBhZ2VkJTIwYmVlZiUyMHN0ZWFrJTIwbWVhdCUyMGRpc3BsYXklMjBzaG93Y2FzZSUyMGdvdXJtZXR8ZW58MHx8fHwxNzkyNjA2MTYxfDA&ixlib=rb-4.1.0&q=85";

const DEFAULT_IMG = { fiorentina: IMG_1, costata: IMG_2, tomahawk: IMG_3 };

export default function Lombate() {
  const { lang } = useLang();
  const photos = usePhotos();
  const [active, setActive] = useState(null);
  const [cuts, setCuts] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/carni`)
      .then((r) => setCuts(r.data))
      .catch(() => setCuts([]));
  }, []);

  const cutImg = (cut) => photos[`lombate-${cut.id}`] || DEFAULT_IMG[cut.id] || IMG_1;
  const loc = (v) => (typeof v === "string" ? v : v?.[lang] || v?.it || "");

  return (
    <section id="lombate" data-testid="lombate-section" className="py-24 lg:py-32 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(110,29,36,0.18),transparent_60%)]" />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="max-w-2xl"
        >
          <span className="eyebrow">{lang === "it" ? "La Vetrina" : "The Display"}</span>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#FAF7F2]">
            {lang === "it" ? (
              <>In alternativa <span className="italic text-[#C9A265]">le nostre lombate</span></>
            ) : (
              <>As an alternative, <span className="italic text-[#C9A265]">our dry-aged steaks</span></>
            )}
          </h2>
          <p className="mt-5 text-base text-[#B0A89C] leading-relaxed">
            {lang === "it"
              ? "In sala, una vetrina refrigerata custodisce le nostre lombate."
              : "In the dining room, a refrigerated display cabinet guards our dry-aging steaks."}
          </p>
        </motion.div>

        {!cuts ? (
          <p data-testid="lombate-loading" className="mt-14 text-[#7A7267] font-cinzel tracking-[0.25em] text-xs uppercase">
            {lang === "it" ? "Caricamento..." : "Loading..."}
          </p>
        ) : (
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {cuts.map((cut, i) => (
              <motion.button
                key={cut.id}
                data-testid={`lombate-card-${cut.id}`}
                onClick={() => setActive(cut)}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.12 }}
                className={`group relative text-left overflow-hidden rounded-2xl border border-[rgba(255,255,255,0.08)] bg-[#171512] hover:border-[rgba(201,162,101,0.5)] hover:shadow-[0_0_50px_rgba(201,162,101,0.15)] transition-all duration-500 ${
                  i === 0 ? "md:row-span-2" : ""
                }`}
              >
                <div className={`overflow-hidden ${i === 0 ? "h-72 md:h-[420px]" : "h-56"}`}>
                  <img
                    src={cutImg(cut)}
                    alt={cut.name}
                    className="w-full h-full object-cover group-hover:scale-[1.06] transition-transform duration-[1.2s]"
                  />
                </div>
                <div className="p-5">
                  <p className="font-serif-display text-xl text-[#FAF7F2] group-hover:text-[#C9A265] transition-colors duration-300">
                    {cut.name}
                  </p>
                  <p className="mt-1 text-[10px] uppercase tracking-[0.25em] text-[#7A7267] font-cinzel">
                    {loc(cut.days)}
                  </p>
                </div>
              </motion.button>
            ))}

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="rounded-2xl bg-[#6E1D24] p-8 flex flex-col justify-center"
            >
              <p className="font-serif-display italic text-2xl text-[#FAF7F2] leading-snug">
                {lang === "it"
                  ? '"Non solo un taglio di carne, ma il frutto del lavoro e della cura dei nostri allevatori di fiducia."'
                  : '"Not just a cut of meat, but the fruit of the work and care of our trusted farmers."'}
              </p>
              <p className="mt-4 text-[11px] uppercase tracking-[0.25em] text-[#FAF7F2]/70 font-cinzel">
                {lang === "it" ? "— La Cucina del Milano" : "— The Milano Kitchen"}
              </p>
            </motion.div>
          </div>
        )}
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="lombate-detail-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center p-6 bg-[#0D0C0A]/85 backdrop-blur-md"
            onClick={() => setActive(null)}
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="relative max-w-lg w-full bg-[#171512] border border-[rgba(201,162,101,0.3)] rounded-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={cutImg(active)} alt={active.name} className="w-full h-56 object-cover" />
              <button
                data-testid="lombate-modal-close"
                onClick={() => setActive(null)}
                className="absolute top-4 right-4 h-9 w-9 rounded-full bg-[#0D0C0A]/70 backdrop-blur flex items-center justify-center text-[#FAF7F2] hover:text-[#C9A265]"
                aria-label="Chiudi"
              >
                <X size={18} />
              </button>
              <div className="p-8">
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#C9A265] font-cinzel">
                  {loc(active.days)}{loc(active.breed) ? ` · ${loc(active.breed)}` : ""}
                </p>
                <h3 className="mt-2 font-serif-display text-3xl text-[#FAF7F2]">{active.name}</h3>
                <p className="mt-4 text-sm text-[#B0A89C] leading-relaxed">{loc(active.note)}</p>
                {active.pairing && (
                  <p className="mt-5 text-xs uppercase tracking-[0.2em] text-[#7A7267] font-cinzel">
                    {lang === "it" ? "In abbinamento:" : "Pairing:"}{" "}
                    <span className="text-[#C9A265]">{active.pairing}</span>
                  </p>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
